<script setup lang="ts">
import { ref, onMounted, computed, onUnmounted } from 'vue'
import { useVideoManagerStore } from '../stores/videoManager'

const props = defineProps<{
  videoSrc: string
  autoplay?: boolean
  isHeader?: boolean
  startMuted?: boolean
  onLoaded?: (src: string) => void
}>()

const videoRef = ref<HTMLVideoElement | null>(null)
const containerRef = ref<HTMLDivElement | null>(null)
const isPlaying = ref(false)
const isMuted = ref(props.startMuted ?? true)
const isFullscreen = ref(false)
const { currentlyPlayingVideo, setCurrentlyPlaying, updateVideoDistance, setHeaderVideo, handleUnmute } = useVideoManagerStore()

const resolvedVideoSrc = computed(() => {
  // Remove any leading ./ or ../ from the path
  const cleanPath = props.videoSrc.replace(/^[./]+/, '')
  // Always use the videos path
  return `/videos/${cleanPath}`
})

const resolvedThumbnailSrc = computed(() => {
  // Remove any leading ./ or ../ from the path and the extension
  const cleanPath = props.videoSrc.replace(/^[./]+/, '').replace(/\.[^/.]+$/, '')
  // Use the thumbnails path
  return `/videos/thumbnails/${cleanPath}.jpg`
})

onMounted(() => {
  if (videoRef.value) {
    videoRef.value.muted = isMuted.value

    // Register as header video if this is the header
    if (props.isHeader && videoRef.value) {
      setHeaderVideo(videoRef.value)
      // For header video, wait for loadedmetadata before playing
      videoRef.value.addEventListener('loadedmetadata', () => {
        if (videoRef.value) {
          videoRef.value.muted = true // Ensure muted for first play
          videoRef.value.play()
            .then(() => {
              isPlaying.value = true
            })
            .catch(error => {
              console.warn('Header video autoplay failed:', error)
            })
        }
      })
    }

    // Listen for state updates from video manager
    videoRef.value.addEventListener('videoStateUpdate', () => {
      if (videoRef.value) {
        isPlaying.value = !videoRef.value.paused
        isMuted.value = videoRef.value.muted
      }
    })

    // Only set up intersection observer for non-header videos
    if (!props.isHeader) {
      if (props.autoplay) {
        // Create intersection observer for continuous autoplay
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (videoRef.value) {
              // Ensure muted state for autoplay
              videoRef.value.muted = true
              isMuted.value = true
              updateVideoDistance(videoRef.value, entry.isIntersecting)
            }
          })
        }, {
          threshold: [0, 0.25, 0.5, 0.75, 1]
        })

        // Start observing the video
        observer.observe(videoRef.value)

        // Update distances on scroll
        const handleScroll = () => {
          if (videoRef.value) {
            updateVideoDistance(videoRef.value, true)
          }
        }
        window.addEventListener('scroll', handleScroll, { passive: true })

        // Cleanup
        onUnmounted(() => {
          window.removeEventListener('scroll', handleScroll)
          observer.disconnect()
        })
      } else {
        // Create one-time intersection observer for initial autoplay
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting && videoRef.value && !isPlaying.value) {
              // Wait for video to be ready
              if (videoRef.value.readyState >= 2) {
                // Ensure video is muted for autoplay
                videoRef.value.muted = true
                isMuted.value = true
                
                videoRef.value.play()
                  .then(() => {
                    isPlaying.value = true
                    setCurrentlyPlaying(videoRef.value)
                  })
                  .catch(error => {
                    console.warn('Autoplay failed:', error)
                  })
              } else {
                // Wait for enough data before playing
                videoRef.value.addEventListener('canplay', () => {
                  if (videoRef.value && !isPlaying.value) {
                    videoRef.value.muted = true
                    isMuted.value = true
                    
                    videoRef.value.play()
                      .then(() => {
                        isPlaying.value = true
                        setCurrentlyPlaying(videoRef.value)
                      })
                      .catch(error => {
                        console.warn('Autoplay failed:', error)
                      })
                  }
                }, { once: true })
              }
              observer.disconnect() // Stop observing after first play attempt
            }
          })
        }, {
          threshold: 0.5 // Play when half the video is visible
        })

        // Start observing the video
        observer.observe(videoRef.value)

        // Cleanup
        onUnmounted(() => {
          observer.disconnect()
        })
      }
    }

    // Cleanup for video state update listener
    onUnmounted(() => {
      if (videoRef.value) {
        videoRef.value.removeEventListener('videoStateUpdate', () => {
          if (videoRef.value) {
            isPlaying.value = !videoRef.value.paused
            isMuted.value = videoRef.value.muted
          }
        })
      }
    })
  }

  document.addEventListener('fullscreenchange', () => {
    isFullscreen.value = !!document.fullscreenElement
  })
})

const togglePlay = () => {
  if (!videoRef.value) return
  
  if (isPlaying.value) {
    videoRef.value.pause()
    isPlaying.value = false
    setCurrentlyPlaying(null)
  } else {
    videoRef.value.play()
      .then(() => {
        isPlaying.value = true
        setCurrentlyPlaying(videoRef.value)
      })
      .catch(error => {
        console.warn('Video play failed:', error)
        isPlaying.value = false
      })
  }
}

const toggleMute = () => {
  if (!videoRef.value) return
  
  videoRef.value.muted = !videoRef.value.muted
  isMuted.value = videoRef.value.muted

  // If unmuting, handle header video pause
  if (!videoRef.value.muted) {
    handleUnmute(videoRef.value)
  }
}

const toggleFullscreen = async () => {
  if (!containerRef.value) return

  try {
    if (!document.fullscreenElement) {
      await containerRef.value.requestFullscreen()
      isFullscreen.value = true
    } else {
      await document.exitFullscreen()
      isFullscreen.value = false
    }
  } catch (err) {
    console.error('Error attempting to toggle full-screen:', err)
  }
}

defineExpose({
  togglePlay,
  toggleMute,
  toggleFullscreen
})

defineOptions({ name: 'VideoPlayer' })
</script> 

<template>
  <div class="relative w-full mb-8 md:mb-0">
    <!-- 16:9 aspect ratio container -->
    <div ref="containerRef" class="relative w-full max-w-[1200px] mx-auto pt-[56.25%] overflow-visible">
      <!-- Video container -->
      <div class="absolute inset-0 rounded-md overflow-hidden">
        <video 
          ref="videoRef"
          loop 
          playsinline
          preload="none"
          @loadeddata="props.onLoaded?.(resolvedVideoSrc)"
          class="absolute top-0 left-0 w-full h-full object-cover"
          :muted="isMuted"
          :poster="resolvedThumbnailSrc"
        >
          <source :src="resolvedVideoSrc" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>

      <!-- Corner Decorations -->
      <!-- Top Left Corner -->
      <div class="absolute opacity-0 z-10 md:opacity-30" style="left: -1px; top: -25px;">
        <div class="h-3 w-[1px] bg-white"></div>
        <div class="w-3 h-[1px] bg-white left-[-18px] top-[18px] absolute"></div>
      </div>

      <!-- Top Right Corner -->
      <div class="absolute opacity-0 z-10 md:opacity-30" style="right: -1px; top: -25px;">
        <div class="h-3 w-[1px] bg-white"></div>
        <div class="w-3 h-[1px] bg-white right-[-18px] top-[18px] absolute"></div>
      </div>

      <!-- Bottom Left Corner -->
      <div class="absolute opacity-0 z-10 md:opacity-30" style="left: -1px; bottom: -25px;">
        <div class="h-3 w-[1px] bg-white"></div>
        <div class="w-3 h-[1px] bg-white left-[-18px] bottom-[18px] absolute"></div>
      </div>

      <!-- Bottom Right Corner -->
      <div class="absolute opacity-0 z-10 md:opacity-30" style="right: -1px; bottom: -25px;">
        <div class="h-3 w-[1px] bg-white"></div>
        <div class="w-3 h-[1px] bg-white right-[-18px] bottom-[18px] absolute"></div>
      </div>

      <!-- Custom Video Controls -->
      <!-- Left Controls -->
      <div class="absolute z-20 bottom-3 left-3 flex items-center space-x-1.5">
        <button @click="togglePlay" class="bg-[#E8E8E8] rounded-[7.5px] p-1.5 opacity-50 hover:opacity-100 transition-opacity">
          <img 
            :src="isPlaying ? '/assets/icons/play.svg' : '/assets/icons/pause.svg'" 
            alt="Play/Pause"
            class="w-[15px] h-[15px]"
            draggable="false"
          />
        </button>
        <button @click="toggleMute" class="bg-[#E8E8E8] rounded-[7.5px] p-1.5 opacity-50 hover:opacity-100 transition-opacity">
          <img 
            :src="isMuted ? '/assets/icons/mute.svg' : '/assets/icons/unmute.svg'" 
            alt="Mute/Unmute"
            class="w-[15px] h-[15px]"
            draggable="false"
          />
        </button>
      </div>

      <!-- Right Controls -->
      <div class="absolute z-20 bottom-3 right-3">
        <button @click="toggleFullscreen" class="bg-[#E8E8E8] rounded-[7.5px] p-1.5 opacity-50 hover:opacity-100 transition-opacity">
          <img 
            src="/assets/icons/fullscreen.svg" 
            alt="Fullscreen"
            class="w-[15px] h-[15px]"
            :class="{ 'invert': isFullscreen }"
            draggable="false"
          />
        </button>
      </div>
    </div>
  </div>
</template>
